// API client for Architecture - Installations, Zones & Equipment
// Uses same pattern as wattelEM.ts
import { translate } from '../i18n';

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

// ============================================
// Types
// ============================================

export interface ZoneEquipmentData {
  id?: number;
  name: string;
  icon?: string;
  type?: string;
  power?: number | null;
  deviceId?: number | null;
  deviceSn?: string | null;
  wattelDeviceType?: string | null;
  positionX: number;
  positionY: number;
}

export interface ZoneData {
  id?: number;
  name: string;
  icon?: string;
  wattelemSn?: string;
  positionX: number;
  positionY: number;
  equipment: ZoneEquipmentData[];
}

export interface InstallationData {
  id: number;
  name: string;
  description?: string;
  status: 'draft' | 'validated' | 'production';
  createdAt: string;
  updatedAt: string;
  zones: (ZoneData & { id: number; createdAt: string; updatedAt: string; equipment: (ZoneEquipmentData & { id: number })[] })[];
}

export interface ArchitectureStats {
  total: number;
  draft: number;
  validated: number;
  production: number;
  totalZones: number;
  totalEquipment: number;
}

export interface DeviceState {
  deviceSn: string;
  online: boolean;
  lastSeen: string | null;
  powerKw: number | null;
  voltageV: number | null;
  currentA: number | null;
  relayState: number | null;
}

export interface CatalogDevice {
  // Identity comes from mqtt_device_states.device_sn (no FK to devices table)
  deviceSn: string;
  deviceName: string | null;
  online?: boolean;
  lastSeen?: string | null;
  powerKw?: number | null;
  voltageV?: number | null;
  currentA?: number | null;
  relayState?: number | null;
  // Legacy fields kept optional for backwards compat with pages that still
  // read them. Not populated by `/catalog-devices` anymore.
  deviceId?: number;
  deviceStatus?: string | null;
  deviceTypeId?: number | null;
  chargePointVendor?: string | null;
  chargePointModel?: string | null;
}


export interface MeterLatest {
  deviceId: number;
  timestamp: string;
  activeFEnergy: number | null;
  activeFPower: number | null;
  activeFPowerTs?: string | null;
  rmsVoltage: number | null;
  rmsCurrent: number | null;
  rmsCurrentTs?: string | null;
  temperature: number | null;
}
export interface SessionEnergy {
  transactionId: number;
  deviceId: number;
  status: string | null;
  isActive: boolean;
  startTime: string | null;
  endTime: string | null;
  meterStart: number | null;
  meterStop: number | null;
  lastMeterEnergy: number | null;
  firstMeterEnergy: number | null;
  energyWh: number | null;
  energyKwh: number | null;
}
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// ============================================
// Auth helpers (same as wattelEM.ts)
// ============================================

function getAuthToken(): string | null {
  return localStorage.getItem('dashboard_zce_token');
}

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Support mode (installer agissant pour une org cliente) : on propage l'org
 * ciblée via ?orgId=, comme le fait bornesService. Renvoie '' si non défini.
 */
function actingOrgQuery(): string {
  const actingOrgId = sessionStorage.getItem('actingClientOrgId');
  return actingOrgId ? `?orgId=${actingOrgId}` : '';
}

function actingOrgId(): number | null {
  const v = sessionStorage.getItem('actingClientOrgId');
  return v ? parseInt(v, 10) : null;
}

/** Erreur HTTP sans message du backend : le texte affiché est le message générique traduit. */
export class ApiStatusError extends Error {}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Seuls les messages métier (chaîne) sont affichés ; une erreur serveur ({ message, code })
    // retombe sur le message générique plutôt que « [object Object] ».
    const err = typeof errorData.error === 'string'
      ? new Error(errorData.error)
      : new ApiStatusError(translate('arch3_api_error', { status: response.status, statusText: response.statusText }));
    // Le statut HTTP est porté par l'erreur : les appelants testent err.status plutôt que le texte.
    throw Object.assign(err, { status: response.status });
  }

  return response.json();
}

// ============================================
// Architecture API Client
// ============================================

export const architectureApi = {
  // Vérifie si un numéro de série est disponible (non utilisé dans une autre
  // installation). excludeInstallationId = l'installation courante (qui a le
  // droit de réutiliser un SN qu'elle détient déjà).
  checkDeviceAvailable: (sn: string, excludeInstallationId?: number | null) => {
    const params = new URLSearchParams({ sn });
    if (excludeInstallationId) params.set('excludeInstallationId', String(excludeInstallationId));
    return fetchApi<{ available: boolean; sn: string }>(`/api/architecture/device-claims/check?${params.toString()}`);
  },

  // List installations (org-scoped côté backend ; ?orgId en support mode)
  getInstallations: () =>
    fetchApi<InstallationData[]>(`/api/architecture/installations${actingOrgQuery()}`),

  // Get single installation
  getInstallation: (id: number) =>
    fetchApi<InstallationData>(`/api/architecture/installations/${id}${actingOrgQuery()}`),

  // Create installation (with nested zones & equipment)
  createInstallation: (data: {
    name: string;
    description?: string;
    status?: 'draft' | 'validated' | 'production';
    zones?: Omit<ZoneData, 'id'>[];
  }) => {
    const orgId = actingOrgId();
    return fetchApi<InstallationData>('/api/architecture/installations', {
      method: 'POST',
      body: JSON.stringify(orgId ? { ...data, orgId } : data),
    });
  },

  // Update installation (full replace of zones)
  updateInstallation: (id: number, data: {
    name?: string;
    description?: string;
    status?: 'draft' | 'validated' | 'production';
    zones?: Omit<ZoneData, 'id'>[];
  }) =>
    fetchApi<InstallationData>(`/api/architecture/installations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Delete installation
  deleteInstallation: (id: number) =>
    fetchApi<{ deleted: boolean }>(`/api/architecture/installations/${id}${actingOrgQuery()}`, {
      method: 'DELETE',
    }),

  // Add zone to installation
  addZone: (installationId: number, data: Omit<ZoneData, 'id' | 'equipment'>) =>
    fetchApi<ZoneData & { id: number }>(`/api/architecture/installations/${installationId}/zones`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update zone (name, wattelemSn, etc.)
  updateZone: (zoneId: number, data: Partial<Omit<ZoneData, 'id' | 'equipment'>>) =>
    fetchApi<ZoneData>(`/api/architecture/zones/${zoneId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Add equipment to zone
  addEquipment: (zoneId: number, data: Omit<ZoneEquipmentData, 'id'>) =>
    fetchApi<ZoneEquipmentData & { id: number }>(`/api/architecture/zones/${zoneId}/equipment`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update equipment (name, type, power, zoneId)
  updateEquipment: (equipmentId: number, data: Partial<ZoneEquipmentData & { zoneId: number }>) =>
    fetchApi<ZoneEquipmentData & { id: number }>(`/api/architecture/equipment/${equipmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Delete zone
  deleteZone: (zoneId: number) =>
    fetchApi<{ deleted: boolean }>(`/api/architecture/zones/${zoneId}`, {
      method: 'DELETE',
    }),

  // Delete equipment
  deleteEquipment: (equipmentId: number) =>
    fetchApi<{ deleted: boolean }>(`/api/architecture/equipment/${equipmentId}`, {
      method: 'DELETE',
    }),

  // Get dashboard stats
  getStats: () =>
    fetchApi<ArchitectureStats>('/api/architecture/stats'),

  // Get live WATTELEM device states
  getDevices: () =>
    fetchApi<DeviceState[]>('/api/architecture/devices'),

  // Get all catalog devices (WattelGo, WattelWall, etc.)
  getCatalogDevices: () =>
    fetchApi<CatalogDevice[]>('/api/architecture/catalog-devices'),

  getLatestMeterValues: (deviceIds: number[]) =>
    fetchApi<MeterLatest[]>(`/api/architecture/meter-values/latest?deviceIds=${deviceIds.join(',')}`),

  getMeterValuesHistory: (deviceId: number, days = 30, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams({ deviceId: String(deviceId) });
    if (startDate && endDate) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else {
      params.set('days', String(days));
    }
    return fetchApi<{ day: string; daily_kwh: number; avg_power_kw: number; max_power_kw: number }[]>(
      `/api/architecture/meter-values/history?${params}`
    );
  },

  getMeterValuesHourly: (deviceId: number, hours = 24) =>
    fetchApi<{ day: string; daily_kwh: number; avg_power_kw: number; max_power_kw: number }[]>(
      `/api/architecture/meter-values/hourly-history?deviceId=${deviceId}&hours=${hours}`
    ),

  // Per-session energy: (last activeFEnergy in meter_values for the transaction) - (meter_start of that transaction).
  getDeviceSessionEnergy: (deviceId: number) =>
    fetchApi<SessionEnergy | null>(`/api/architecture/session-energy?deviceId=${deviceId}`),
};
